import { useState, useRef, useCallback, useEffect } from "react";
import { Link } from "react-router-dom";
import Webcam from "react-webcam";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { 
  Camera, Upload, Loader2, Search, AlertTriangle, 
  CheckCircle, ShoppingCart, HelpCircle, MapPin, Package, X, Focus
} from "lucide-react";
import { toast } from "sonner";
import { useSmartCapture } from "@/hooks/useSmartCapture";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Video constraints with autofocus for better scanning
const VIDEO_CONSTRAINTS = {
  facingMode: "environment",
  width: { ideal: 1920 },
  height: { ideal: 1080 },
  focusMode: "continuous",
  advanced: [{ focusMode: "continuous" }]
};

// Haptic feedback helper - with iOS fallback using AudioContext
const triggerHaptic = (pattern = [50]) => {
  // Try standard Vibration API first (Android, some browsers)
  if ('vibrate' in navigator) {
    try {
      navigator.vibrate(pattern);
      return;
    } catch (e) {
      // Vibration failed, try fallback
    }
  }
  
  // iOS fallback: Create a subtle audio "tick" using AudioContext
  try {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (AudioContextClass) {
      const audioCtx = new AudioContextClass();
      const oscillator = audioCtx.createOscillator();
      const gainNode = audioCtx.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioCtx.destination);
      
      oscillator.frequency.value = 150;
      oscillator.type = 'sine';
      
      const duration = Math.min(pattern[0] || 50, 30) / 1000;
      gainNode.gain.setValueAtTime(0, audioCtx.currentTime);
      gainNode.gain.linearRampToValueAtTime(0.1, audioCtx.currentTime + duration / 2);
      gainNode.gain.linearRampToValueAtTime(0, audioCtx.currentTime + duration);
      
      oscillator.start(audioCtx.currentTime);
      oscillator.stop(audioCtx.currentTime + duration);
      
      setTimeout(() => audioCtx.close(), 100);
    }
  } catch (e) {
    // Audio feedback not available, fail silently
  }
};

export default function PublicToolCheck() {
  const webcamRef = useRef(null);
  const fileInputRef = useRef(null);
  const resultRef = useRef(null);
  const cameraContainerRef = useRef(null);
  
  const [serialNumber, setSerialNumber] = useState("");
  const [manufacturer, setManufacturer] = useState("");
  const [extractedToolType, setExtractedToolType] = useState("");
  const [extractedModelNumber, setExtractedModelNumber] = useState("");
  const [searching, setSearching] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [location, setLocation] = useState(null);
  const [showResultOverlay, setShowResultOverlay] = useState(false);
  const [focusPoint, setFocusPoint] = useState(null);
  const [showSerialHelp, setShowSerialHelp] = useState(false);
  const [showDewaltWarning, setShowDewaltWarning] = useState(false);
  const [showStolenWarning, setShowStolenWarning] = useState(true);

  // Scroll camera into view when it opens (for iOS)
  useEffect(() => {
    if (showCamera && cameraContainerRef.current) {
      setTimeout(() => {
        cameraContainerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    }
  }, [showCamera]);

  // Tap-to-focus handler
  const handleTapToFocus = async (e) => {
    if (!webcamRef.current?.video) return;
    
    const video = webcamRef.current.video;
    const stream = video.srcObject;
    if (!stream) return;
    
    const track = stream.getVideoTracks()[0];
    if (!track) return;
    
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    
    setFocusPoint({ x: e.clientX - rect.left, y: e.clientY - rect.top });
    triggerHaptic([20]);
    setTimeout(() => setFocusPoint(null), 1000);
    
    try {
      const capabilities = track.getCapabilities();
      if (capabilities.focusMode) {
        const constraints = { advanced: [{ focusMode: "manual" }] };
        if (capabilities.focusDistance) {
          const minFocus = capabilities.focusDistance.min || 0;
          const maxFocus = capabilities.focusDistance.max || 1;
          constraints.advanced[0].focusDistance = minFocus + (maxFocus - minFocus) * 0.2;
        }
        await track.applyConstraints(constraints);
        setTimeout(async () => {
          try {
            await track.applyConstraints({ advanced: [{ focusMode: "continuous" }] });
          } catch (e) {}
        }, 2000);
      }
    } catch (error) {
      console.log("Focus control not available");
    }
  };

  // Get user's location on mount
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy
          });
        },
        (error) => {
          console.log("Location access denied or unavailable");
        },
        { enableHighAccuracy: true, timeout: 10000 }
      );
    }
  }, []);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!serialNumber.trim()) {
      toast.error("Please enter a serial number");
      return;
    }
    if (!manufacturer.trim()) {
      toast.error("Please enter a manufacturer");
      return;
    }
    
    setSearching(true);
    setResult(null);
    setShowResultOverlay(false);
    
    try {
      const response = await fetch(`${API}/public/check-tool`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          serial_number: serialNumber.trim(),
          manufacturer: manufacturer.trim(),
          model_number: extractedModelNumber.trim() || undefined,
          location: location
        })
      });
      
      const data = await response.json();
      setResult(data);
      
      // Show overlay and haptic feedback based on result
      if (data.found) {
        setShowResultOverlay(true);
        if (data.status === "Stolen") {
          triggerHaptic([200, 100, 200, 100, 200]); // Strong alert for stolen
        } else {
          triggerHaptic([50, 50, 100]); // Normal feedback
        }
      } else {
        triggerHaptic([30, 50, 30]); // Not found feedback
        setShowResultOverlay(true);
      }
      
      // Scroll to result after a short delay
      setTimeout(() => {
        resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
      
    } catch (error) {
      toast.error("Search failed. Please try again.");
      triggerHaptic([100, 50, 100]);
    } finally {
      setSearching(false);
    }
  };

  // Smart capture callback - receives the best blob
  const handleSmartCapture = useCallback(async (blob) => {
    setShowCamera(false);
    setScanning(true);
    await processOCR(blob);
  }, [location]);

  // Smart capture hook
  const smartCapture = useSmartCapture({
    webcamRef,
    active: showCamera,
    onCapture: handleSmartCapture,
  });

  const captureImage = useCallback(async () => {
    // Manual capture via smart capture
    smartCapture.manualCapture();
  }, [smartCapture]);

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }
    
    setScanning(true);
    await processOCR(file);
  };

  const processOCR = async (imageBlob) => {
    try {
      const formData = new FormData();
      formData.append("file", imageBlob, "scan.jpg");
      if (location) {
        formData.append("latitude", location.latitude.toString());
        formData.append("longitude", location.longitude.toString());
      }

      const response = await fetch(`${API}/public/scan-tool`, {
        method: "POST",
        body: formData
      });

      const data = await response.json();
      setResult(data);
      
      if (data.extracted_serial) {
        setSerialNumber(data.extracted_serial);
      }
      if (data.extracted_manufacturer) {
        setManufacturer(data.extracted_manufacturer);
      }
      if (data.extracted_tool_type) {
        setExtractedToolType(data.extracted_tool_type);
      }
      if (data.extracted_model_number) {
        setExtractedModelNumber(data.extracted_model_number);
      }
      
      // Show overlay and haptic feedback for OCR results
      if (data.found) {
        setShowResultOverlay(true);
        if (data.status === "Stolen") {
          triggerHaptic([200, 100, 200, 100, 200]); // Strong alert for stolen
        } else {
          triggerHaptic([50, 50, 100]); // Normal feedback
        }
        // Scroll to result
        setTimeout(() => {
          resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 100);
      } else if (data.extracted_serial || data.extracted_manufacturer) {
        triggerHaptic([30, 50]); // Partial scan feedback
        setShowResultOverlay(true);
        setTimeout(() => {
          resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 100);
      } else {
        // Nothing detected from scan
        triggerHaptic([50, 30, 50]); // "Not found" feedback
        toast.error("No tool information detected. Try adjusting the angle or lighting, or enter details manually.");
      }

      // DeWalt serial number warning
      if (data.extracted_manufacturer && data.extracted_manufacturer.toLowerCase().includes("dewalt") && !data.extracted_serial) {
        setShowDewaltWarning(true);
      }
    } catch (error) {
      toast.error("Scan failed. Please try again.");
      triggerHaptic([100, 50, 100]);
    } finally {
      setScanning(false);
    }
  };

  const getStatusDisplay = () => {
    if (!result) return null;
    
    // Show message if OCR found partial info
    if (!result.found && (result.extracted_serial || result.extracted_manufacturer)) {
      const hasSerial = result.extracted_serial;
      const hasMfr = result.extracted_manufacturer;
      
      if (!hasSerial || !hasMfr) {
        return (
          <Card className="border-2 border-yellow-500 bg-yellow-900/20 mb-4">
            <CardContent className="p-4 md:p-4">
              <p className="text-yellow-300 text-base md:text-sm leading-relaxed">
                <strong>Partial scan:</strong> We detected {hasMfr ? `manufacturer "${result.extracted_manufacturer}"` : ""} 
                {hasMfr && hasSerial ? " and " : ""}
                {hasSerial ? `serial "${result.extracted_serial}"` : ""}.
                {!hasMfr && " Manufacturer not detected."} 
                {!hasSerial && " Serial number not detected."}
                {" "}Please fill in the missing field below and search manually.
              </p>
            </CardContent>
          </Card>
        );
      }
    }
    
    if (!result.found) {
      return (
        <Card className="border-2 border-yellow-500 bg-yellow-900/30">
          <CardContent className="p-6 text-center">
            <HelpCircle className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
            <h3 className="text-2xl font-heading font-bold text-yellow-300 mb-2">NOT FOUND</h3>
            <p className="text-lg text-white mb-2">{result.manufacturer} — {result.serial_number}</p>
            <p className="text-yellow-200/80">
              This tool is not registered in our system. This doesn't necessarily mean anything is wrong - 
              the owner may not have registered it yet.
            </p>
            
            {/* AI-detected info for reference */}
            {(result.extracted_manufacturer || extractedToolType || extractedModelNumber) && (
              <div className="mt-4 pt-4 border-t border-yellow-700/50">
                <p className="text-xs text-yellow-300/70 mb-2">AI Scan Detection</p>
                <div className="flex flex-wrap justify-center gap-3">
                  {result.extracted_manufacturer && (
                    <div className="bg-yellow-950/50 px-3 py-2 rounded border border-yellow-800/50">
                      <p className="text-xs text-yellow-300/60">Manufacturer</p>
                      <p className="text-sm text-yellow-200">{result.extracted_manufacturer}</p>
                    </div>
                  )}
                  {extractedToolType && (
                    <div className="bg-yellow-950/50 px-3 py-2 rounded border border-yellow-800/50">
                      <p className="text-xs text-yellow-300/60">Tool Type</p>
                      <p className="text-sm text-yellow-200">{extractedToolType}</p>
                    </div>
                  )}
                  {extractedModelNumber && (
                    <div className="bg-yellow-950/50 px-3 py-2 rounded border border-yellow-800/50">
                      <p className="text-xs text-yellow-300/60">Model</p>
                      <p className="text-sm text-yellow-200">{extractedModelNumber}</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      );
    }

    const { status, manufacturer, tool_type } = result;

    if (status === "Stolen") {
      return (
        <Card className="border-2 border-red-500 bg-red-900/30">
          <CardContent className="p-6 text-center">
            <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h3 className="text-2xl font-heading font-bold text-red-400 mb-2">REPORTED STOLEN</h3>
            <p className="text-lg text-white mb-2">{manufacturer} {tool_type}</p>
            {result.model_number && <p className="text-sm text-red-200/80 -mt-1 mb-2">Model: {result.model_number}</p>}
            <p className="text-red-300 mt-4">
              This tool has been reported as stolen. Do not purchase this item. 
              If you have information about this tool, please contact your local police.
            </p>
            
            {/* AI-detected info for confirmation */}
            {(result.extracted_manufacturer || extractedToolType || extractedModelNumber) && (
              <div className="mt-4 pt-4 border-t border-red-700/50">
                <p className="text-xs text-red-300/70 mb-2">AI Scan Detection</p>
                <div className="flex flex-wrap justify-center gap-3">
                  {result.extracted_manufacturer && (
                    <div className="bg-red-950/50 px-3 py-2 rounded border border-red-800/50">
                      <p className="text-xs text-red-300/60">Manufacturer</p>
                      <p className="text-sm text-red-200">{result.extracted_manufacturer}</p>
                    </div>
                  )}
                  {extractedToolType && (
                    <div className="bg-red-950/50 px-3 py-2 rounded border border-red-800/50">
                      <p className="text-xs text-red-300/60">Tool Type</p>
                      <p className="text-sm text-red-200">{extractedToolType}</p>
                    </div>
                  )}
                  {extractedModelNumber && (
                    <div className="bg-red-950/50 px-3 py-2 rounded border border-red-800/50">
                      <p className="text-xs text-red-300/60">Model</p>
                      <p className="text-sm text-red-200">{extractedModelNumber}</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      );
    }

    if (status === "Active") {
      return (
        <Card className="border-2 border-green-500 bg-green-900/30">
          <CardContent className="p-6 text-center">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h3 className="text-2xl font-heading font-bold text-green-400 mb-2">REGISTERED - ACTIVE</h3>
            <p className="text-lg text-white mb-2">{manufacturer} {tool_type}</p>
            {result.model_number && <p className="text-sm text-green-200/80 -mt-1 mb-2">Model: {result.model_number}</p>}
            <p className="text-green-200 mt-4">
              This tool is currently registered as active by its owner.
            </p>
            <div className="mt-4 p-4 bg-green-900/50 rounded border border-green-600">
              <p className="text-green-100">
                <strong>Buying this tool?</strong> Please remind the current owner to update 
                the status of this tool to "Sold" in their TOOLIO account after the sale is complete.
              </p>
            </div>
            
            {/* AI-detected info for confirmation */}
            {(result.extracted_manufacturer || extractedToolType || extractedModelNumber) && (
              <div className="mt-4 pt-4 border-t border-green-700/50">
                <p className="text-xs text-green-300/70 mb-2">AI Scan Detection</p>
                <div className="flex flex-wrap justify-center gap-3">
                  {result.extracted_manufacturer && (
                    <div className="bg-green-950/50 px-3 py-2 rounded border border-green-800/50">
                      <p className="text-xs text-green-300/60">Manufacturer</p>
                      <p className="text-sm text-green-200">{result.extracted_manufacturer}</p>
                    </div>
                  )}
                  {extractedToolType && (
                    <div className="bg-green-950/50 px-3 py-2 rounded border border-green-800/50">
                      <p className="text-xs text-green-300/60">Tool Type</p>
                      <p className="text-sm text-green-200">{extractedToolType}</p>
                    </div>
                  )}
                  {extractedModelNumber && (
                    <div className="bg-green-950/50 px-3 py-2 rounded border border-green-800/50">
                      <p className="text-xs text-green-300/60">Model</p>
                      <p className="text-sm text-green-200">{extractedModelNumber}</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      );
    }

    if (status === "Sold") {
      return (
        <Card className="border-2 border-purple-500 bg-purple-900/30">
          <CardContent className="p-6 text-center">
            <ShoppingCart className="w-16 h-16 text-purple-500 mx-auto mb-4" />
            <h3 className="text-2xl font-heading font-bold text-purple-400 mb-2">SOLD</h3>
            <p className="text-lg text-white mb-2">{manufacturer} {tool_type}</p>
            {result.model_number && <p className="text-sm text-purple-200/80 -mt-1 mb-2">Model: {result.model_number}</p>}
            <p className="text-purple-200 mt-4">
              This tool has been marked as sold by the previous owner. 
              You can register it as your own after purchase.
            </p>
            <Link to="/register">
              <Button className="mt-4 bg-purple-600 hover:bg-purple-700">
                Register This Tool
              </Button>
            </Link>
            
            {/* AI-detected info for confirmation */}
            {(result.extracted_manufacturer || extractedToolType || extractedModelNumber) && (
              <div className="mt-4 pt-4 border-t border-purple-700/50">
                <p className="text-xs text-purple-300/70 mb-2">AI Scan Detection</p>
                <div className="flex flex-wrap justify-center gap-3">
                  {result.extracted_manufacturer && (
                    <div className="bg-purple-950/50 px-3 py-2 rounded border border-purple-800/50">
                      <p className="text-xs text-purple-300/60">Manufacturer</p>
                      <p className="text-sm text-purple-200">{result.extracted_manufacturer}</p>
                    </div>
                  )}
                  {extractedToolType && (
                    <div className="bg-purple-950/50 px-3 py-2 rounded border border-purple-800/50">
                      <p className="text-xs text-purple-300/60">Tool Type</p>
                      <p className="text-sm text-purple-200">{extractedToolType}</p>
                    </div>
                  )}
                  {extractedModelNumber && (
                    <div className="bg-purple-950/50 px-3 py-2 rounded border border-purple-800/50">
                      <p className="text-xs text-purple-300/60">Model</p>
                      <p className="text-sm text-purple-200">{extractedModelNumber}</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      );
    }

    if (status === "Discarded") {
      return (
        <Card className="border-2 border-gray-500 bg-gray-900/50">
          <CardContent className="p-6 text-center">
            <CheckCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-2xl font-heading font-bold text-gray-300 mb-2">DISCARDED</h3>
            <p className="text-lg text-white mb-2">{manufacturer} {tool_type}</p>
            {result.model_number && <p className="text-sm text-gray-300/80 -mt-1 mb-2">Model: {result.model_number}</p>}
            <p className="text-gray-300 mt-4">
              This tool has been marked as discarded by the previous owner, typically due to damage, 
              end of useful life, or being beyond repair.
            </p>
            <div className="mt-4 p-4 bg-yellow-900/50 rounded border border-yellow-600">
              <p className="text-yellow-100">
                <strong>Please exercise caution</strong> — ensure this tool is safe and in working condition 
                before use. Discarded tools may have been disposed of for safety reasons.
              </p>
            </div>
            <Link to="/register">
              <Button className="mt-4" variant="secondary">
                Understood, Register This Tool
              </Button>
            </Link>
            
            {/* AI-detected info for confirmation */}
            {(result.extracted_manufacturer || extractedToolType || extractedModelNumber) && (
              <div className="mt-4 pt-4 border-t border-gray-700/50">
                <p className="text-xs text-gray-500 mb-2">AI Scan Detection</p>
                <div className="flex flex-wrap justify-center gap-3">
                  {result.extracted_manufacturer && (
                    <div className="bg-gray-950/50 px-3 py-2 rounded border border-gray-700/50">
                      <p className="text-xs text-gray-500">Manufacturer</p>
                      <p className="text-sm text-gray-300">{result.extracted_manufacturer}</p>
                    </div>
                  )}
                  {extractedToolType && (
                    <div className="bg-gray-950/50 px-3 py-2 rounded border border-gray-700/50">
                      <p className="text-xs text-gray-500">Tool Type</p>
                      <p className="text-sm text-gray-300">{extractedToolType}</p>
                    </div>
                  )}
                  {extractedModelNumber && (
                    <div className="bg-gray-950/50 px-3 py-2 rounded border border-gray-700/50">
                      <p className="text-xs text-gray-500">Model</p>
                      <p className="text-sm text-gray-300">{extractedModelNumber}</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      );
    }

    if (status === "Written Off") {
      return (
        <Card className="border-2 border-orange-500 bg-orange-900/30">
          <CardContent className="p-6 text-center">
            <AlertTriangle className="w-16 h-16 text-orange-500 mx-auto mb-4" />
            <h3 className="text-2xl font-heading font-bold text-orange-400 mb-2">WRITTEN OFF</h3>
            <p className="text-lg text-white mb-2">{manufacturer} {tool_type}</p>
            {result.model_number && <p className="text-sm text-orange-200/80 -mt-1 mb-2">Model: {result.model_number}</p>}
            <p className="text-orange-200 mt-4">
              This tool was previously reported stolen and has since been written off by the owner, 
              typically following an insurance claim. This tool should not be for sale.
            </p>
            
            {/* AI-detected info for confirmation */}
            {(result.extracted_manufacturer || extractedToolType || extractedModelNumber) && (
              <div className="mt-4 pt-4 border-t border-orange-700/50">
                <p className="text-xs text-orange-300/70 mb-2">AI Scan Detection</p>
                <div className="flex flex-wrap justify-center gap-3">
                  {result.extracted_manufacturer && (
                    <div className="bg-orange-950/50 px-3 py-2 rounded border border-orange-800/50">
                      <p className="text-xs text-orange-300/60">Manufacturer</p>
                      <p className="text-sm text-orange-200">{result.extracted_manufacturer}</p>
                    </div>
                  )}
                  {extractedToolType && (
                    <div className="bg-orange-950/50 px-3 py-2 rounded border border-orange-800/50">
                      <p className="text-xs text-orange-300/60">Tool Type</p>
                      <p className="text-sm text-orange-200">{extractedToolType}</p>
                    </div>
                  )}
                  {extractedModelNumber && (
                    <div className="bg-orange-950/50 px-3 py-2 rounded border border-orange-800/50">
                      <p className="text-xs text-orange-300/60">Model</p>
                      <p className="text-sm text-orange-200">{extractedModelNumber}</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      );
    }

    return null;
  };

  // Get overlay background color based on result status
  const getOverlayColor = () => {
    if (!result) return 'transparent';
    if (!result.found) return 'rgba(234, 179, 8, 0.3)'; // yellow
    if (result.status === 'Stolen') return 'rgba(239, 68, 68, 0.4)'; // red
    if (result.status === 'Active') return 'rgba(34, 197, 94, 0.3)'; // green
    if (result.status === 'Sold') return 'rgba(168, 85, 247, 0.3)'; // purple
    if (result.status === 'Written Off') return 'rgba(249, 115, 22, 0.3)'; // orange
    return 'rgba(107, 114, 128, 0.3)'; // gray default
  };

  const dismissOverlay = () => {
    setShowResultOverlay(false);
  };

  return (
    <div className={`min-h-screen transition-colors duration-500 ${showResultOverlay ? '' : 'bg-background'}`} style={{ backgroundColor: showResultOverlay ? getOverlayColor() : undefined }}>
      {/* Result Overlay - Mobile only */}
      {showResultOverlay && result && (
        <div className="md:hidden fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
          <div className="w-full max-w-sm">
            {/* Close button */}
            <button 
              onClick={dismissOverlay}
              className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/20 flex items-center justify-center text-white"
            >
              <X className="w-6 h-6" />
            </button>
            
            {/* Result Card */}
            <div ref={resultRef}>
              {getStatusDisplay()}
            </div>
            
            {/* Dismiss hint */}
            <p className="text-center text-white/70 text-sm mt-4">
              Tap anywhere or X to close
            </p>
          </div>
          
          {/* Click outside to dismiss */}
          <div className="absolute inset-0 -z-10" onClick={dismissOverlay} />
        </div>
      )}

      {/* Header */}
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-40">
        <div className="flex items-center justify-between px-4 py-3 lg:px-8">
          <Link to="/" className="flex items-center gap-2">
            <img src="/toolio-logo-new.png" alt="TOOLIO" className="h-8" />
            
          </Link>
          <div className="flex items-center gap-2">
            <Link to="/login">
              <Button variant="ghost" size="sm" className="h-10 md:h-8">Login</Button>
            </Link>
            <Link to="/register">
              <Button size="sm" className="btn-primary h-10 md:h-8">Sign Up</Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="p-4 lg:p-8 max-w-2xl mx-auto">
        <div className="text-center mb-6 md:mb-8">
          <h1 className="text-2xl md:text-3xl font-heading font-bold uppercase mb-2">Check a Tool</h1>
          <p className="text-lg md:text-xl text-yellow-400 font-bold">
            Buying a pre-owned tool?<br />Check to see if it has been logged as stolen
          </p>
          {location && (
            <p className="text-xs text-muted-foreground mt-2 flex items-center justify-center gap-1">
              <MapPin className="w-3 h-3" /> Location enabled for security
            </p>
          )}
        </div>

        {/* Stolen Serial Warning Popup Dialog */}
        <Dialog open={showStolenWarning} onOpenChange={setShowStolenWarning}>
          <DialogContent className="max-w-md bg-yellow-500 border-yellow-600 text-black">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-3 text-black font-heading uppercase text-2xl">
                <AlertTriangle className="w-10 h-10" />
                Warning
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <p className="text-black font-bold text-lg">
                A damaged or removed serial number may indicate the tool is stolen and its identity is being masked.
              </p>
              <p className="text-black/80 text-base">
                Proceed with caution when purchasing tools with illegible, scratched, or missing serial numbers.
              </p>
            </div>
            <Button 
              onClick={() => setShowStolenWarning(false)}
              className="w-full bg-black text-yellow-500 hover:bg-black/80 font-bold"
            >
              I Understand
            </Button>
          </DialogContent>
        </Dialog>

        {/* Combined Scan & Search Section */}
        <Card className="border-border bg-card mb-4 md:mb-6">
          <CardContent className="p-4 md:p-6">
            {showCamera ? (
              <div ref={cameraContainerRef} className="space-y-4">
                {/* Scan guidance - above viewfinder */}
                <div className="text-center py-2">
                  <p className="text-primary text-base font-semibold">Hold your device approx 20cm away from the label. No need to fill the viewfinder.</p>
                </div>
                {/* Camera View Container - constrained height on mobile */}
                <div 
                  className="relative w-full overflow-hidden rounded-xl md:rounded border border-border cursor-pointer" 
                  style={{ height: '35vh', maxHeight: '35vh' }}
                  onClick={handleTapToFocus}
                >
                  <Webcam
                    ref={webcamRef}
                    audio={false}
                    screenshotFormat="image/jpeg"
                    screenshotQuality={0.92}
                    videoConstraints={VIDEO_CONSTRAINTS}
                    className="absolute inset-0 w-full h-full object-cover"
                    style={{ transform: `scale(${smartCapture.zoom})`, transformOrigin: 'center center' }}
                  />
                  {/* Camera overlay with quality-reactive frame */}
                  <div className="camera-overlay">
                    <div className="camera-frame" data-quality={smartCapture.quality}>
                      <div className="camera-corner camera-corner-tl" />
                      <div className="camera-corner camera-corner-tr" />
                      <div className="camera-corner camera-corner-bl" />
                      <div className="camera-corner camera-corner-br" />
                    </div>
                  </div>
                  {/* Zoom indicator */}
                  {smartCapture.zoom > 1 && (
                    <div className="absolute top-2 right-2 bg-black/60 text-white text-xs px-2 py-0.5 rounded-full pointer-events-none">
                      {smartCapture.zoom.toFixed(1)}x
                    </div>
                  )}
                  {/* Tap to focus indicator */}
                  {focusPoint && (
                    <div 
                      className="absolute w-16 h-16 border-2 border-yellow-400 rounded-lg pointer-events-none animate-pulse"
                      style={{ 
                        left: focusPoint.x - 32, 
                        top: focusPoint.y - 32,
                        boxShadow: '0 0 20px rgba(250, 204, 21, 0.5)'
                      }}
                    >
                      <Focus className="w-6 h-6 text-yellow-400 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
                    </div>
                  )}
                  {/* Quality hint */}
                  <div className={`absolute bottom-2 left-1/2 -translate-x-1/2 text-white text-xs px-3 py-1 rounded-full pointer-events-none transition-colors duration-300 ${
                    smartCapture.quality === 'green' ? 'bg-green-600/80' : 
                    smartCapture.quality === 'yellow' ? 'bg-yellow-600/80' : 'bg-red-600/80'
                  }`}>
                    {smartCapture.capturing ? 'Capturing...' :
                     smartCapture.hint === 'ready' ? 'Ready - press capture' :
                     smartCapture.hint === 'move-back' ? 'Move back slightly' :
                     smartCapture.hint === 'checking' ? 'Checking label...' :
                     'Align label - adjust lighting'}
                  </div>
                </div>
                
                {/* Buttons - always visible below camera */}
                <div className="flex gap-2">
                  <Button 
                    onClick={captureImage} 
                    className="flex-1 btn-primary h-14 md:h-10"
                    disabled={scanning || smartCapture.capturing}
                  >
                    {smartCapture.capturing ? (
                      <><Loader2 className="w-5 h-5 md:w-4 md:h-4 mr-2 animate-spin" /> Capturing...</>
                    ) : (
                      <><Camera className="w-5 h-5 md:w-4 md:h-4 mr-2" /> Capture Now</>
                    )}
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={() => setShowCamera(false)}
                    className="h-14 md:h-10"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Processing Overlay - Shows when scanning after camera closes */}
                {scanning ? (
                  <div className="bg-primary/10 border-2 border-primary/50 rounded-xl p-8 text-center">
                    <Loader2 className="w-12 h-12 text-primary animate-spin mx-auto mb-4" />
                    <p className="text-lg font-bold text-primary">Checking Tool...</p>
                    <p className="text-sm text-muted-foreground mt-2">Our AI is reading the label and searching our database</p>
                    <p className="text-xs text-yellow-400 mt-3">This can take up to 12 seconds</p>
                  </div>
                ) : (
                  <>
                    {/* Scan Button - Full width on mobile */}
                    <Button
                      className="w-full h-16 btn-primary flex items-center justify-center gap-2 rounded-xl text-base"
                      onClick={() => setShowCamera(true)}
                      disabled={scanning}
                    >
                      <Camera className="w-6 h-6" />
                      <span>Scan Tool Label</span>
                    </Button>
                    
                    {/* Upload link */}
                    <Button
                      variant="ghost"
                      className="w-full h-8 text-muted-foreground hover:text-yellow-400 text-sm"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={scanning}
                    >
                      <Upload className="w-4 h-4 mr-2" /> Or upload an image
                    </Button>
                  </>
                )}
                
                {/* Divider */}
                <div className="flex items-center gap-3 py-1 md:py-2">
                  <div className="flex-1 h-px bg-border"></div>
                  <span className="text-xs text-muted-foreground uppercase">or enter manually</span>
                  <div className="flex-1 h-px bg-border"></div>
                </div>
                
                {/* Manual Search Form */}
                <form onSubmit={handleSearch} className="space-y-3">
                  <div>
                    <Label htmlFor="manufacturer" className="text-sm block mb-2">Manufacturer *</Label>
                    <Input
                      id="manufacturer"
                      value={manufacturer}
                      onChange={(e) => setManufacturer(e.target.value)}
                      placeholder="e.g., DeWalt, Makita, Milwaukee"
                      className="bg-background border-input h-12 md:h-10"
                    />
                  </div>
                  <div>
                    <div className="flex items-center justify-between">
                      <Label htmlFor="serial" className="text-sm">Serial Number *</Label>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowSerialHelp(true)}
                        className="text-primary hover:text-primary/80 text-xs px-2 py-0 h-auto"
                      >
                        <HelpCircle className="w-3 h-3 mr-1" />
                        Help finding the Serial Number
                      </Button>
                    </div>
                    <Input
                      id="serial"
                      value={serialNumber}
                      onChange={(e) => setSerialNumber(e.target.value)}
                      placeholder="Enter the tool's serial number"
                      className="bg-background border-input font-mono h-12 md:h-10"
                    />
                  </div>
                  <div>
                    <Label htmlFor="modelNumber" className="text-sm block mb-2">Model Number</Label>
                    <Input
                      id="modelNumber"
                      data-testid="model-number-input"
                      value={extractedModelNumber}
                      onChange={(e) => setExtractedModelNumber(e.target.value)}
                      placeholder="e.g., DCF887, DHP482 (optional)"
                      className="bg-background border-input font-mono h-12 md:h-10"
                    />
                  </div>
                  <Button 
                    type="submit" 
                    className={`w-full h-12 md:h-10 text-base md:text-sm mt-12 md:mt-4 transition-all duration-200 ${
                      manufacturer.trim() && serialNumber.trim() 
                        ? 'bg-green-600 hover:bg-green-700 text-white' 
                        : 'bg-muted text-muted-foreground cursor-not-allowed'
                    }`}
                    disabled={searching || !manufacturer.trim() || !serialNumber.trim()}
                  >
                    {searching ? (
                      <><Loader2 className="w-5 h-5 md:w-4 md:h-4 mr-2 animate-spin" /> Checking...</>
                    ) : (
                      <><Search className="w-5 h-5 md:w-4 md:h-4 mr-2" /> Check Tool</>
                    )}
                  </Button>
                </form>
                
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileUpload}
                  className="hidden"
                />
              </div>
            )}
          </CardContent>
        </Card>

        {/* Result Display - Desktop only (mobile uses overlay) */}
        <div className="hidden md:block" ref={resultRef}>
          {result && getStatusDisplay()}
        </div>

        {/* Info Section */}
        <div className="mt-8 text-center text-sm text-muted-foreground">
          <p className="mb-2">
            TOOLIO helps protect tool owners from theft and helps buyers avoid stolen goods.
          </p>
          <p>
            <Link to="/register" className="text-primary hover:underline">Create a free account</Link>
            {" "}to register and protect your own tools.
          </p>
        </div>
      </main>

      {/* DeWalt Serial Number Warning Dialog */}
      <Dialog open={showDewaltWarning} onOpenChange={setShowDewaltWarning}>
        <DialogContent className="max-w-sm p-0 overflow-hidden rounded-xl flex flex-col" style={{ maxHeight: 'min(75vh, 75dvh)' }} data-testid="dewalt-warning-dialog">
          <div className="bg-[#febd17] px-4 py-4 flex items-center gap-3 flex-shrink-0">
            <img
              src="https://res.cloudinary.com/dzx4iay2g/image/upload/v1774575010/toolio/dewalt/iclbx1dmya0ugh3tdjka.jpg"
              alt="DeWalt logo"
              className="h-12 w-auto"
            />
            <div>
              <DialogTitle className="text-black font-heading uppercase text-base">DeWalt Serial Number</DialogTitle>
              <p className="text-black/70 text-sm">Brand-specific scanning issue</p>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            <p className="text-sm text-muted-foreground leading-relaxed">
              Some DeWalt tools <strong className="text-foreground">do not include the needed serial number on the main rating label</strong>. Instead the number is engraved either:
            </p>
            <ul className="text-sm text-muted-foreground leading-relaxed list-disc pl-5 space-y-1">
              <li>Inside the battery compartment</li>
              <li>Near the motor housing</li>
              <li>On the tool base on certain corded or stationary models</li>
            </ul>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Do not confuse the Serial Number with the Date Code, it is the <strong className="text-foreground">engraved Serial Number</strong> that uniquely identifies your tool.
            </p>
            <div className="rounded-lg overflow-hidden border border-border">
              <img
                src="https://res.cloudinary.com/dzx4iay2g/image/upload/v1774443669/toolio/dewalt/jyadkfqeawqy0lm2whlz.jpg"
                alt="DeWalt serial number location under battery compartment"
                className="w-full h-52 object-contain bg-black/20"
              />
              <p className="text-xs text-muted-foreground p-2 text-center italic">Date code and serial number inside the battery compartment</p>
            </div>
            <div className="bg-[#febd17]/10 border border-[#febd17]/30 rounded-lg p-3">
              <p className="text-sm leading-relaxed">
                <strong className="text-foreground">Please enter the serial number manually</strong> in the search fields. Check under the battery or on the base of the tool.
              </p>
            </div>
          </div>
          <div className="px-4 pb-4 pt-2 border-t border-border flex-shrink-0">
            <Button
              className="w-full h-10 font-bold text-sm"
              style={{ backgroundColor: '#febd17', color: '#000' }}
              onClick={() => setShowDewaltWarning(false)}
              data-testid="dewalt-warning-ok-btn"
            >
              Got it, I'll enter it manually
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Serial Number Help Dialog */}
      <Dialog open={showSerialHelp} onOpenChange={setShowSerialHelp}>
        <DialogContent className="max-w-lg max-h-[85vh] p-0">
          <DialogHeader className="p-6 pb-2">
            <DialogTitle className="font-heading uppercase text-lg">Where to Find the Serial Number</DialogTitle>
            <p className="text-sm text-muted-foreground">
              Serial numbers are usually found on a label or engraved plate on the tool. Check inside battery compartments too - many manufacturers place labels or engraved serial numbers there.
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              <strong className="text-yellow-400">Tip:</strong> If the serial is engraved and hard to read, try rubbing a little chalk, Tipp-Ex or a finger dab of paint over it, then wipe off the excess - this makes the numbers stand out.
            </p>
          </DialogHeader>
          <ScrollArea className="h-[60vh] px-6 pb-6">
            <div className="space-y-6">
              
              {/* Bosch */}
              <div className="border-b border-border pb-4">
                <h3 className="font-heading text-base font-bold text-primary mb-2">Bosch</h3>
                <p className="text-sm text-muted-foreground mb-2">
                  Look for a silver or white label on the main body of the tool, usually near the motor housing or on the bottom. The serial number is typically a 10-digit number starting with "0" or "3".
                </p>
                <div className="text-xs text-muted-foreground italic">Common locations: Bottom of tool, near the air vents, or on the battery compartment.</div>
              </div>

              {/* DeWalt */}
              <div className="border-b border-border pb-4">
                <h3 className="font-heading text-base font-bold text-primary mb-2">DeWalt</h3>
                <p className="text-sm text-muted-foreground mb-2">
                  DeWalt serial numbers are found on a yellow and black label, typically on the motor housing or handle. The format is usually 8-10 alphanumeric characters.
                </p>
                <div className="text-xs text-muted-foreground italic">Common locations: Side of the motor housing, bottom of the handle, or near / under the battery slot.</div>
              </div>

              {/* Festool */}
              <div className="border-b border-border pb-4">
                <h3 className="font-heading text-base font-bold text-primary mb-2">Festool</h3>
                <p className="text-sm text-muted-foreground mb-2">
                  Festool tools have a data plate with serial number, usually on the underside or rear of the tool. Look for "S/N" or "Serial No." followed by numbers.
                </p>
                <div className="text-xs text-muted-foreground italic">Common locations: Underside of tool, rear housing, or inside the battery compartment.</div>
              </div>

              {/* Hikoki (formerly Hitachi) */}
              <div className="border-b border-border pb-4">
                <h3 className="font-heading text-base font-bold text-primary mb-2">Hikoki / Hitachi</h3>
                <p className="text-sm text-muted-foreground mb-2">
                  Serial numbers are on a silver label on the main body. Older Hitachi tools may have the number engraved. Format is typically alphanumeric, 8-12 characters.
                </p>
                <div className="text-xs text-muted-foreground italic">Common locations: Side of motor housing, near the cord entry point, or on the base plate.</div>
              </div>

              {/* Makita */}
              <div className="border-b border-border pb-4">
                <h3 className="font-heading text-base font-bold text-primary mb-2">Makita</h3>
                <p className="text-sm text-muted-foreground mb-2">
                  Makita serial numbers are on a white or silver label, usually on the side of the tool body near the motor. The serial is typically 6-8 digits, sometimes with a letter prefix.
                </p>
                <div className="text-xs text-muted-foreground italic">Common locations: Side of motor housing, under the battery, or near the air intake.</div>
              </div>

              {/* Milwaukee */}
              <div className="border-b border-border pb-4">
                <h3 className="font-heading text-base font-bold text-primary mb-2">Milwaukee</h3>
                <p className="text-sm text-muted-foreground mb-2">
                  Look for a white label with barcode on the tool body. The serial number is below the barcode, typically starting with letters followed by numbers (e.g., "A12B123456").
                </p>
                <div className="text-xs text-muted-foreground italic">Common locations: Main body near the trigger, bottom of handle, or inside the battery compartment.</div>
              </div>

              {/* Metabo */}
              <div className="border-b border-border pb-4">
                <h3 className="font-heading text-base font-bold text-primary mb-2">Metabo</h3>
                <p className="text-sm text-muted-foreground mb-2">
                  Metabo places serial numbers on a rating plate, usually on the motor housing. The number is typically 8-10 digits and may include manufacturing date codes.
                </p>
                <div className="text-xs text-muted-foreground italic">Common locations: Side of the tool body, near the gear housing, or on the base.</div>
              </div>

              {/* Paslode */}
              <div className="border-b border-border pb-4">
                <h3 className="font-heading text-base font-bold text-primary mb-2">Paslode</h3>
                <p className="text-sm text-muted-foreground mb-2">
                  Paslode nail guns have serial numbers engraved or on a label on the main body. Look near the magazine or on the side of the combustion chamber housing.
                </p>
                <div className="text-xs text-muted-foreground italic">Common locations: Side of the body, near the fuel cell compartment, or on the handle.</div>
              </div>

              {/* Ryobi */}
              <div className="border-b border-border pb-4">
                <h3 className="font-heading text-base font-bold text-primary mb-2">Ryobi</h3>
                <p className="text-sm text-muted-foreground mb-2">
                  Ryobi serial numbers are on a green or white label on the tool body. The format varies but is usually 10-14 characters including letters and numbers.
                </p>
                <div className="text-xs text-muted-foreground italic">Common locations: Bottom of the tool, side of motor housing, or near the battery connection.</div>
              </div>

              {/* Snap-on */}
              <div className="border-b border-border pb-4">
                <h3 className="font-heading text-base font-bold text-primary mb-2">Snap-on</h3>
                <p className="text-sm text-muted-foreground mb-2">
                  Snap-on tools typically have serial numbers engraved on the tool itself or on a small metal plate. Air tools often have the number near the air inlet.
                </p>
                <div className="text-xs text-muted-foreground italic">Common locations: Engraved on the body, on a riveted plate, or near the drive mechanism.</div>
              </div>

              {/* Stihl */}
              <div className="border-b border-border pb-4">
                <h3 className="font-heading text-base font-bold text-primary mb-2">Stihl</h3>
                <p className="text-sm text-muted-foreground mb-2">
                  Stihl serial numbers are on a silver plate on the crankcase or motor housing. Chainsaws have it near the rear handle. Format is typically 9 digits.
                </p>
                <div className="text-xs text-muted-foreground italic">Common locations: Side of the engine/motor, near the starter, or on the crankcase.</div>
              </div>

              {/* Can't find it? */}
              <div className="bg-muted/50 rounded-lg p-4">
                <h3 className="font-heading text-base font-bold mb-2">Can't Find the Serial Number?</h3>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• Check the original box or receipt</li>
                  <li>• Look inside battery compartments</li>
                  <li>• Check under removable covers or plates</li>
                  <li>• Some tools have it engraved rather than on a label</li>
                  <li>• Contact the manufacturer with your model number</li>
                </ul>
              </div>

            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  );
}
